import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  GridType,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { DEFAULT_GRID_BOT_STRATEGY } from "@shared/features/bots/BotForm/common/constants";
import { getGridsPriceDifference } from "@shared/features/bots/BotForm/common/utils";
import {
  AmountType,
  BotType,
  OrderBaseline,
  StrategyStyleType,
  WorkspaceType,
} from "@shared/types";
import { selectWorkspace } from "@shared/slices/common.slice";
import { PRACTICE_SYMBOLS } from "features/onboarding/guided/intent";
import { useBotFormDefaultAmount } from "@shared/features/bots/BotForm/common/hooks";
import { OnboardingBotSetupContext } from "features/onboarding/guided/OnboardingBotSetup.context";

/** Longest wait for the amount check before the values count as in. */
const READY_FALLBACK_MS = 1500;

const PRACTICE_AMOUNT = 1000;
const PRACTICE_ORDERS = 30;

export type PracticePreset = "dca" | "grid" | "tradingview" | "hedging";
/** The bot type each preset fills (the form switches type as it loads). */
const PRESET_BOT_TYPE: Record<PracticePreset, BotType> = {
  dca: BotType.ASAP,
  grid: BotType.GRID,
  tradingview: BotType.TRADING_VIEW,
  hedging: BotType.HEDGING,
};

const PracticeBotPreset = ({ preset }: { preset: PracticePreset }) => {
  const form = useContext(BotFormContext)!;
  const workspace = useSelector(selectWorkspace);
  const applied = useRef(false);
  const selected = useRef(false);
  const [presetApplied, setPresetApplied] = useState(false);
  const fitted = useRef(false);
  const { getDefaultAmountValue } = useBotFormDefaultAmount();
  const onPresetReady = useContext(OnboardingBotSetupContext)?.onPresetReady;
  useEffect(() => {
    if (!presetApplied || !onPresetReady) return;
    const timer = window.setTimeout(onPresetReady, READY_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [presetApplied, onPresetReady]);
  useEffect(() => {
    if (
      selected.current ||
      workspace !== WorkspaceType.DEMO ||
      form.isEdit ||
      !form.account ||
      typeof form.account === "symbol" ||
      !form.symbolsMetadata
    )
      return;
    const symbols = form.symbolsMetadata.getAccountActiveSymbols(
      form.account.client_id
    );
    const symbol = PRACTICE_SYMBOLS.find((candidate) =>
      symbols?.includes(candidate)
    );
    if (!symbol) return;
    selected.current = true;
    form.setSymbols([symbol]);
  }, [form, workspace]);
  useEffect(() => {
    if (
      applied.current ||
      workspace !== WorkspaceType.DEMO ||
      form.isEdit ||
      form.isLoading ||
      form.isFetching ||
      !form.symbolPriceData ||
      !form.entryStrategy ||
      !form.account ||
      typeof form.account === "symbol"
    )
      return;
    if (form.botType !== PRESET_BOT_TYPE[preset]) return;
    if (preset === "grid" && !form.gridsData?.priceRange?.max) return;
    applied.current = true;
    form.setAmountType(AmountType.FIXED_USD_AMOUNT);
    form.setAmountValue(PRACTICE_AMOUNT);
    form.amountChangedRef.current = true;
    form.setMultiplier(1);
    // TradingView and Hedging keep their own strategy defaults; the symbol
    // (and with it the name) and the amount are what they were missing.
    if (preset === "grid") applyGridPreset(form);
    else if (preset === "dca") applyDcaPreset(form);
    form.addFieldToInitialize?.("entryStrategy");
    setPresetApplied(true);
  }, [form, preset, workspace]);
  // The exchange's minimum order can need more than the practice amount (a
  // 30-grid bot on Binance Futures BTC/USDT needs about 3,341 USD): once the
  // preset's orders are in, raise the amount to that minimum (to the next
  // 100 USD), so the prepared bot is valid as it stands.
  useEffect(() => {
    if (
      !presetApplied ||
      fitted.current ||
      !form.entryStrategy ||
      typeof form.entryStrategy === "symbol" ||
      ((preset === "dca" || preset === "grid") &&
        form.entryStrategy.number_of_orders !== PRACTICE_ORDERS)
    )
      return;
    const amount = getDefaultAmountValue(AmountType.FIXED_USD_AMOUNT);
    if (!amount) return;
    fitted.current = true;
    const fittedAmount = Math.ceil(amount.minBotAmount / 100) * 100;
    if (
      amount.minBotAmount > PRACTICE_AMOUNT &&
      !(amount.availableAmount && fittedAmount > amount.availableAmount)
    ) {
      form.setAmountValue(fittedAmount);
      form.addFieldToInitialize?.("amountValue");
    }
    onPresetReady?.();
  });
  return null;
};

export default PracticeBotPreset;
const applyGridPreset = (
  form: NonNullable<React.ContextType<typeof BotFormContext>>
): void => {
  const { data: price, precision } = form.symbolPriceData!;
  const gridsData = {
    gridType: GridType.ARITHMETIC,
    numGrids: String(PRACTICE_ORDERS),
    priceRange: {
      min: (price * 0.925).toFixed(precision.price),
      max: (price * 1.075).toFixed(precision.price),
    },
  };
  form.setGridsData(gridsData);
  form.setEntryStrategy({
    ...DEFAULT_GRID_BOT_STRATEGY,
    number_of_orders: PRACTICE_ORDERS,
    number_of_active_orders: 3,
    price_difference: getGridsPriceDifference(gridsData),
  });
};
const applyDcaPreset = (
  form: NonNullable<React.ContextType<typeof BotFormContext>>
): void => {
  form.toggleField(ToggleField.STOP, false);
  form.toggleField(ToggleField.FIRST_ENTRY, true);
  form.setEntryStrategy({
    strategy_style: StrategyStyleType.DCA,
    number_of_orders: PRACTICE_ORDERS,
    number_of_active_orders: 1,
    first_order_percentage: 10,
    amount_scale: 1.03,
    price_difference: 0.4,
    price_scale: 1.05,
  });
  form.setDefaultTakeProfit({
    percent: "2",
    baseline: OrderBaseline.AVERAGE_ENTRIES,
  });
};
